class SubcollectionsSwiper extends HTMLElement {
  constructor(){
    super();
    this.swiperElement = this.querySelector('.collection__subcollection.swiper');
    this.swiperOptions = {
      slidesPerView: 3.5,
      spaceBetween: 10,
      breakpoints: {
        1024: {
          slidesPerView: "auto",
          spaceBetween: 16,
        },
      },
    };
    if (this.dataset.subcollectionsStyle === 'chips') {
      this.swiperOptions = {
        slidesPerView: 'auto',
        spaceBetween: 8,
        breakpoints: {
          1024: {
            spaceBetween: 14,
          },
        },
      };
    }
    this.scrollToSubcollection = this.hasAttribute('data-scroll-to-subcollection');
    this.sticky = this.hasAttribute('data-sticky');
    this.bar = this.querySelector('[data-subcollections-bar]');
    this.isStuck = false;
    this.scrollFrame = null;
    this.lockedLink = null;
    this.unlockTimer = null;
    this.links = Array.from(this.querySelectorAll('[data-subcollection-handle]'));
    this.initialActiveLink = this.querySelector('.active-subcollection');
    this.onClick = this.onClick.bind(this);
    this.scrollToHash = this.scrollToHash.bind(this);
    this.requestScrollUpdate = this.requestScrollUpdate.bind(this);
  }

  connectedCallback() {
    this.swiperInit();

    if ((this.sticky && this.bar) || this.scrollToSubcollection) {
      window.addEventListener('scroll', this.requestScrollUpdate, { passive: true });
      window.addEventListener('resize', this.requestScrollUpdate);
      this.requestScrollUpdate();
    }

    if (this.scrollToSubcollection) {
      this.addEventListener('click', this.onClick);

      // Wait for images to load so the target position is final
      if (document.readyState === 'complete') {
        this.scrollToHash();
      } else {
        window.addEventListener('load', this.scrollToHash, { once: true });
      }
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    window.removeEventListener('load', this.scrollToHash);
    window.removeEventListener('scroll', this.requestScrollUpdate);
    window.removeEventListener('resize', this.requestScrollUpdate);
    cancelAnimationFrame(this.scrollFrame);
    clearTimeout(this.unlockTimer);
    if (this.bar) this.setStuck(false);
  }

  swiperInit() {
    if (this.swiperElement) {
      this.swiper = new Swiper(this.swiperElement, this.swiperOptions);
    }
  }

  onClick(event) {
    const link = event.target.closest('[data-subcollection-handle]');
    if (!link) return;

    event.preventDefault();

    const handle = link.dataset.subcollectionHandle;
    if (this.scrollToSection(handle)) {
      history.replaceState(null, '', `#${handle}`);

      // Keep the clicked item active while the smooth scroll passes other sections
      this.lockedLink = link;
      this.setActive(link);
      this.scheduleUnlock();
    }
  }

  scrollToHash() {
    const handle = decodeURIComponent(window.location.hash.slice(1));
    if (handle) this.scrollToSection(handle);
  }

  scrollToSection(handle) {
    const target = document.querySelector(`[data-custom-collection="${CSS.escape(handle)}"]`);
    if (!target) return false;

    const stickyOffset = this.sticky && this.bar ? this.bar.offsetHeight : 0;
    const top = target.getBoundingClientRect().top + window.scrollY - this.getHeaderOffset() - stickyOffset;
    window.scrollTo({ top, behavior: 'smooth' });
    return true;
  }

  getHeaderOffset() {
    if (!document.documentElement.classList.contains('sticky-header-enabled')) return 0;

    const headerHeight = getComputedStyle(document.documentElement).getPropertyValue('--height-header');
    return parseInt(headerHeight, 10) || 0;
  }

  requestScrollUpdate() {
    if (this.lockedLink) this.scheduleUnlock();
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      if (this.sticky && this.bar) this.updateSticky();
      if (this.scrollToSubcollection && !this.lockedLink) this.updateActive();
    });
  }

  // Released once scrolling has been idle for a moment
  scheduleUnlock() {
    clearTimeout(this.unlockTimer);
    this.unlockTimer = setTimeout(() => {
      this.lockedLink = null;
      this.updateActive();
    }, 150);
  }

  // Activates the item whose section has most recently scrolled under the
  // header (and the pinned bar); falls back to the server-rendered active item.
  updateActive() {
    const line = this.getHeaderOffset() + (this.isStuck ? this.bar.offsetHeight : 0) + 2;
    let activeLink = null;
    let activeTop = -Infinity;

    this.links.forEach((link) => {
      const target = document.querySelector(`[data-custom-collection="${CSS.escape(link.dataset.subcollectionHandle)}"]`);
      if (!target) return;

      const top = target.getBoundingClientRect().top;
      if (top <= line && top > activeTop) {
        activeLink = link;
        activeTop = top;
      }
    });

    this.setActive(activeLink || this.initialActiveLink);
  }

  setActive(activeLink) {
    if (this.activeLink === activeLink) return;
    this.activeLink = activeLink;

    this.links.forEach((link) => link.classList.toggle('active-subcollection', link === activeLink));

    if (activeLink) this.revealSlide(activeLink);
  }

  // Slide the swiper only when the active item is outside the visible area
  revealSlide(slide) {
    if (!this.swiper || !this.swiperElement) return;

    const slideRect = slide.getBoundingClientRect();
    const swiperRect = this.swiperElement.getBoundingClientRect();
    if (slideRect.left >= swiperRect.left && slideRect.right <= swiperRect.right) return;

    const index = this.swiper.slides.indexOf(slide);
    if (index !== -1) this.swiper.slideTo(index);
  }

  // The bar is pinned with position: fixed rather than position: sticky so it
  // can stay pinned past its own section, through the sections it scrolls to.
  updateSticky() {
    const offset = this.getHeaderOffset();
    const hostRect = this.getBoundingClientRect();
    const boundaryBottom = this.getStickyBoundary().getBoundingClientRect().bottom;
    const shouldStick = hostRect.top <= offset && boundaryBottom > offset + this.bar.offsetHeight;

    if (shouldStick !== this.isStuck) this.setStuck(shouldStick);

    if (this.isStuck) {
      this.bar.style.top = `${offset}px`;
      this.bar.style.left = `${hostRect.left}px`;
      this.bar.style.width = `${hostRect.width}px`;
    }
  }

  setStuck(stuck) {
    this.isStuck = stuck;

    if (stuck) {
      // Reserve the bar's space so the content below doesn't jump
      this.style.height = `${this.offsetHeight}px`;
    } else {
      this.style.height = '';
      this.bar.style.top = '';
      this.bar.style.left = '';
      this.bar.style.width = '';
    }

    this.bar.classList.toggle('is-stuck', stuck);
    document.documentElement.classList.toggle('subcollections-stuck', stuck);
    document.documentElement.style.setProperty('--subcollections-sticky-height', stuck ? `${this.bar.offsetHeight}px` : '0px');
  }

  // Stay pinned until the end of the own section or, when scrolling to
  // subcollection sections, until the end of the last of those sections.
  getStickyBoundary() {
    let boundary = this.closest('.shopify-section') || this.parentElement;
    if (!this.scrollToSubcollection) return boundary;

    this.querySelectorAll('[data-subcollection-handle]').forEach((link) => {
      const target = document.querySelector(`[data-custom-collection="${CSS.escape(link.dataset.subcollectionHandle)}"]`);
      const section = target && (target.closest('.shopify-section') || target);
      if (section && boundary.compareDocumentPosition(section) & Node.DOCUMENT_POSITION_FOLLOWING) {
        boundary = section;
      }
    });

    return boundary;
  }
}

if (!window.customElements.get("subcollections-swiper")) {
  window.customElements.define("subcollections-swiper", SubcollectionsSwiper );
}
